"use client";

import React, { useState, useMemo } from "react";
import Select from "react-select";
import ScheduleModal from "./ScheduleModal";
import toast from "react-hot-toast";
import whatsApp from "../../../assets/img/admin/whatsapp.png";
import SmsExcelModal from "./SmsExcelModal";
import { customStyles } from "../../../utils/index";
import { postSms } from "../../../services/smsTemplateServices";
import { MessageCircle } from "lucide-react";

const SmsMetaApiForm = ({ templates, groups, refreshSchedule }) => {
  const [sendTo, setSendTo] = useState("group");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [sampleMessage, setSampleMessage] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState({
    value: "",
    label: "",
    totalUsers: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [templateType, setTemplateType] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedSendOption, setSelectedSendOption] = useState("schedule");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);

  const formattedScheduledTime = scheduleDate
    ? scheduleDate.toLocaleString("en-IN", {
        weekday: "long",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  const replaceTemplateParams = (message, params) => {
    let result = message;
    params.forEach((param) => {
      const valueToShow = param.value.trim() || `{${param.position}}`;
      const regex = new RegExp(`{{\\s*${param.position}\\s*}}`, "g");
      result = result.replace(regex, valueToShow);
    });
    return result;
  };

  const displayMessage = useMemo(() => {
    return sampleMessage
      ? replaceTemplateParams(sampleMessage, templateParams)
      : "";
  }, [sampleMessage, templateParams]);

  const groupOptions = groups.map((g) => ({
    value: g.value,
    label: g.label,
    totalUsers: g.totalUsers ?? 0,
  }));

  const templateOptions = templates.map((t) => ({
    value: t.templateId,
    label: t.templateName,
  }));

  const handleParamChange = (index, value) => {
    const newParams = [...templateParams];
    newParams[index].value = value;
    setTemplateParams(newParams);
  };

  const filtered = groups.find((g) => g.value === selectedGroup.value);

  const handleExcelDataSubmit = (data) => {
    setExcelData(data);
    setIsExcelModalOpen(false);
  };

  const handleTemplateChange = async (option) => {
    setSelectedTemplate(option);
    if (!option?.value) return;

    try {
      const selected = templates.find((t) => t.templateId === option.value);

      if (!selected) {
        toast.error("Selected template not found");
        return;
      }

      setTemplateName(selected.templateName || "");
      setSampleMessage(selected.sampleMessage || "");

      if (Array.isArray(selected.placeholders)) {
        const params = selected.placeholders.map((p) => ({
          position: p.position,
          key: p.label,
          value: "",
        }));
        setTemplateParams(params);
      } else {
        setTemplateParams([]);
      }
    } catch (err) {
      toast.error("Failed to fetch template details");
      setTemplateName("");
      setSampleMessage("");
      setTemplateParams([]);
    }
  };

  const sendMetaSms = async () => {
    setIsLoading(true);
    try {
      const isSchedule = selectedSendOption === "schedule";

      const payload = {
        message: displayMessage,
        templateId: selectedTemplate?.value,
        type: isSchedule ? "schedule" : "now",
      };

      if (isSchedule) {
        payload.schedule_time = formattedScheduledTime;
      }

      if (sendTo === "group") {
        const numbers = (filtered?.users || [])
          .map((user) => user.phone)
          .join(",");
        payload.msisdn = numbers;
      } else if (sendTo === "individual") {
        payload.msisdn = phone;
      }

      const response = await postSms(payload);
      toast.success(response.data.message || "SMS sent successfully");
      refreshSchedule();
    } catch (error) {
      console.error("SMS Error:", error);
      toast.error(error?.response?.data?.message || "Failed to send SMS");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
        className="max-w-lg w-full bg-white p-4 mt-4 rounded-2xl border border-gray-200 text-base font-medium text-black"
      >
        {/* Send To Type */}
        <div className="flex mb-4 justify-normal flex-col items-start relative w-full">
          <p>Send to</p>
          <div className="flex gap-5">
            {["group", "individual"].map((val) => (
              <label
                key={val}
                className="flex items-center cursor-pointer relative"
              >
                <input
                  type="radio"
                  name="group"
                  value={val}
                  checked={sendTo === val}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="appearance-none sr-only"
                />
                <div className="w-5 h-5 absolute left-0 rounded-full bg-[#d9d9d9] flex items-center justify-center">
                  <div
                    className={`w-3 h-3 rounded-full transition ${
                      sendTo === val ? "bg-[#4362C1]" : "bg-[#C0C4D1]"
                    }`}
                  />
                </div>
                <span className="ml-2 text-sm font-medium text-black">
                  {val === "individual" ? "By Individual" : "By Group"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Group Select */}
        {sendTo === "group" && (
          <div className="mb-4">
            <Select
              options={groupOptions}
              value={selectedGroup.value ? selectedGroup : null}
              onChange={(option) => setSelectedGroup(option)}
              styles={customStyles}
              components={{ IndicatorSeparator: () => null }}
              placeholder="Select a group"
            />
            {filtered && (
              <p className="text-xs text-black font-normal mt-1">
                This group contains {filtered.totalUsers ?? 0} users{" "}
                <span className="text-blue underline cursor-pointer">
                  View list
                </span>
              </p>
            )}
          </div>
        )}

        {/* Individual Inputs */}
        {sendTo === "individual" && (
          <div className="space-y-3 mb-4">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 h-[40px] border border-[#0000003D]/[24%] placeholder:text-black rounded-lg text-sm focus:outline-none bg-[#FAFAFA] focus:ring-1 focus:ring-blue"
            />
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <MessageCircle size={18} color="#4F22D8" />
                <div className="h-6 w-px bg-gray-300"></div>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number "
                className="w-full pl-12 pr-3 h-[40px] placeholder:text-black border border-[#0000003D]/[24%] rounded-lg text-sm focus:outline-none bg-[#FAFAFA] focus:ring-1 focus:ring-blue"
              />
            </div>
          </div>
        )}

        {/* Template Selection */}
        <div className="border border-[#E0E0E0] rounded-[10px] p-3 mb-4">
          <label className="block text-sm mb-2">Select Template</label>
          <Select
            options={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateChange}
            styles={customStyles}
            components={{ IndicatorSeparator: () => null }}
            placeholder="Search or select template"
          />

          {sampleMessage && (
            <div className="mt-4">
              <label className="block text-sm mb-2">Sample Message</label>
              <textarea
                rows={4}
                value={displayMessage}
                readOnly
                className="w-full border border-[#0000003D] bg-[#FAFAFA] rounded-[10px] text-black font-normal px-3 py-[10px] text-sm"
              />
            </div>
          )}

          {/* Template Params for both types */}
          {templateParams.length > 0 && sendTo === "individual" && (
            <div className="mt-4">
              <label className="block text-sm mb-2">
                Fill Template Parameters ({templateType})
              </label>
              <div className="space-y-3">
                {templateParams.map((param, index) => (
                  <div key={param.key} className="flex flex-col">
                    <label className="text-sm font-medium mb-1">
                      Varibale position {param.position} label {param.key}
                    </label>
                    <input
                      type="text"
                      value={param.value}
                      onChange={(e) => handleParamChange(index, e.target.value)}
                      placeholder={`Enter value for ${param.key}`}
                      className="border border-[#0000003D] bg-white rounded-[10px] text-black font-normal px-3 h-10 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {templateParams.length > 0 && sendTo === "group" && selectedGroup && (
            <div className="mt-4">
              <button
                onClick={() => setIsExcelModalOpen(true)}
                className="w-full bg-[#4F22D8] text-white font-semibold py-2 rounded-md  transition"
              >
                Fill Parameters via Excel
              </button>
              {excelData.length > 0 && (
                <p className="text-xs text-green mt-2">
                  Excel data loaded for {excelData.length} users
                </p>
              )}
            </div>
          )}
        </div>

        {/* Schedule Section */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">
            Schedule / Send
          </label>
          {["Schedule For Later", "Send Now"].map((val) => {
            const valueKey = val === "Schedule For Later" ? "schedule" : "now";
            return (
              <label key={valueKey} className="radio-item">
                <input
                  type="radio"
                  name="sendOption"
                  value={valueKey}
                  checked={selectedSendOption === valueKey}
                  onChange={(e) => setSelectedSendOption(e.target.value)}
                />
                <span className="radio-circle" />
                <span className="radio-label">{val}</span>
              </label>
            );
          })}
          {selectedSendOption === "schedule" && (
            <div
              className="mt-4 text-xs text-[#4F22D8] font-medium cursor-pointer"
              onClick={() => setIsScheduleModalOpen(true)}
            >
              {scheduleDate
                ? `Message Scheduled On ${formattedScheduledTime}`
                : "Select Date & Time"}
            </div>
          )}
        </div>

        {/* Final Submit */}
        <button
          disabled={
            isLoading ||
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !phone)
          }
          onClick={sendMetaSms}
          className={`w-full bg-[#4F22D8] text-white font-semibold py-2 rounded-md transition ${
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !phone)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#3b51a3] cursor-pointer"
          }`}
        >
          {isLoading ? (
            <div className="flex justify-center items-center">
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              Sending...
            </div>
          ) : selectedSendOption === "schedule" ? (
            "Schedule Via Meta API"
          ) : (
            "Send Via Meta API"
          )}
        </button>
      </div>

      {/* Modals */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        initialDate={scheduleDate}
        onConfirm={(date) => {
          setScheduleDate(date);
          setIsScheduleModalOpen(false);
        }}
      />

      <SmsExcelModal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
        onSubmit={handleExcelDataSubmit}
        templateParams={templateParams}
        groupUsers={filtered?.users || []}
        setTemplateParams={setTemplateParams}
      />
    </>
  );
};

export default SmsMetaApiForm;
